import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { ApiError } from "../lib/api";

export function ErrorText({ error }: { error: unknown }) {
  if (!error) return null;
  // A fetch that never reached the API (offline, blocked, server down) is a TypeError.
  const msg = error instanceof ApiError ? error.message : error instanceof TypeError ? "Couldn't reach SVENEX. Check your connection and try again in a minute." : "Something went wrong.";
  const raw = error instanceof ApiError && error.fields ? { ...error.fields } : {};
  // "Try again in 4 minutes", not a raw field.
  const wait = Number(raw.retry_after_seconds);
  delete raw.retry_after_seconds;
  const fields = Object.entries(raw);
  return (
    <div className="error" role="alert">
      {wait > 0 ? `Too many attempts. Try again in ${wait < 90 ? `${Math.ceil(wait)} seconds` : `${Math.ceil(wait / 60)} minutes`}.` : msg}
      {fields.length > 0 && (
        <ul>
          {fields.map(([k, v]) => (
            <li key={k}>
              <b>{k.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())}</b>: {v}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Loading is a quiet placeholder: nothing for the first moment (most loads
 *  finish by then), then soft bars. No "Loading…" text on screen; screen
 *  readers still hear it. */
export function Loading({ rows = 3 }: { rows?: number }) {
  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), 250); return () => clearTimeout(t); }, []);
  return (
    <div className="skeleton" role="status" aria-busy="true">
      <span className="sr-only">Loading</span>
      {show && Array.from({ length: rows }, (_, i) => <i key={i} style={{ width: `${[92, 76, 84, 64, 88][i % 5]}%` }} />)}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

export function PageHeader({ title, actions }: { title: string; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <h1>{title}</h1>
      <div className="actions">{actions}</div>
    </header>
  );
}

// Form wraps submit handling, pending state and error display.
export function Form({ onSubmit, children, submitLabel }: { onSubmit: (data: FormData) => Promise<unknown>; children: ReactNode; submitLabel: string }) {
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => { if (!done) return; const t = setTimeout(() => setDone(false), 1400); return () => clearTimeout(t); }, [done]);
  async function handle(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await onSubmit(new FormData(form));
      form.reset();
      setDone(true);
    } catch (err) {
      setError(err);
    } finally {
      setPending(false);
    }
  }
  // The button keeps its size: a spinner while saving, a tick when it's done.
  return (
    <form onSubmit={handle} className="form">
      {children}
      <ErrorText error={error} />
      <button type="submit" disabled={pending} aria-busy={pending || undefined} className={pending ? "is-busy" : done ? "is-done" : undefined}>
        {submitLabel}
      </button>
      <span className="sr-only" role="status">{pending ? "Working…" : done ? "Done" : ""}</span>
    </form>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

export const str = (d: FormData, k: string) => String(d.get(k) ?? "").trim();

/** countLabel shows big counts as "99+". */
export const countLabel = (n: number) => (n > 99 ? "99+" : String(n));

/** Collapsible keeps long lists short: the first few items, then a button to
 *  see the rest ("Showing 5 of 32"). */
export function Collapsible<T>({ items, first = 5, render, className = "list", noun = "" }: { items: T[]; first?: number; render: (item: T) => ReactNode; className?: string; noun?: string }) {
  const [open, setOpen] = useState(false);
  const shown = open ? items : items.slice(0, first);
  const more = items.length > first;
  return (
    <>
      <ul className={className}>{shown.map(render)}</ul>
      {more && (
        <button type="button" className="show-more" aria-expanded={open} onClick={() => setOpen(!open)}>
          <span>{open ? `Showing all ${countLabel(items.length)}${noun && " " + noun}` : `Showing ${first} of ${countLabel(items.length)}${noun && " " + noun}`}</span>
          <svg viewBox="0 0 24 24" aria-hidden><path d="M7 10l5 5 5-5" /></svg>
        </button>
      )}
    </>
  );
}
